import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts OKLCH color values to hexadecimal string
 * @param oklchString - OKLCH color string (e.g., "oklch(0.393 0.095 152.535)")
 * @returns Hexadecimal color string (e.g., "#0d542b")
 */
export function oklchToHex(oklchString: string): string {
  // Parse OKLCH values
  const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/)
  if (!match) {
    throw new Error("Invalid OKLCH format")
  }

  const L = parseFloat(match[1]) // Lightness (0-1)
  const C = parseFloat(match[2]) // Chroma
  const H = parseFloat(match[3]) // Hue (degrees)

  // Convert OKLCH to OKLab
  const hRad = (H * Math.PI) / 180
  const aLab = C * Math.cos(hRad)
  const bLab = C * Math.sin(hRad)

  // Convert OKLab to linear RGB
  // Using the OKLab to linear RGB conversion matrix
  const l = L + 0.3963377774 * aLab + 0.2158037573 * bLab
  const m = L - 0.1055613458 * aLab - 0.0638541728 * bLab
  const s = L - 0.0894841775 * aLab - 1.291485548 * bLab

  // Apply non-linear transformation
  const l3 = l * l * l
  const m3 = m * m * m
  const s3 = s * s * s

  // Convert to linear RGB
  const rLinear = +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3
  const gLinear = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3
  const bLinear = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3

  // Apply gamma correction (linear RGB to sRGB)
  const gamma = (c: number) => {
    if (c <= 0.0031308) {
      return 12.92 * c
    }
    return 1.055 * Math.pow(c, 1 / 2.4) - 0.055
  }

  const r = Math.max(0, Math.min(1, gamma(rLinear)))
  const g = Math.max(0, Math.min(1, gamma(gLinear)))
  const b = Math.max(0, Math.min(1, gamma(bLinear)))

  // Convert to hex
  const toHex = (value: number) => {
    const hex = Math.round(value * 255).toString(16)
    return hex.length === 1 ? "0" + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Converts hexadecimal color string to OKLCH
 * @param hexString - Hexadecimal color string (e.g., "#0d542b" or "0d542b")
 * @returns OKLCH color string (e.g., "oklch(0.393 0.095 152.535)")
 */
export function hexToOklch(hexString: string): string {
  // Remove # if present
  const hex = hexString.replace("#", "")

  if (hex.length !== 6) {
    throw new Error("Invalid hex format")
  }

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  // Convert sRGB to linear RGB (inverse gamma)
  const invGamma = (c: number) => {
    if (c <= 0.04045) {
      return c / 12.92
    }
    return Math.pow((c + 0.055) / 1.055, 2.4)
  }

  const rLinear = invGamma(r)
  const gLinear = invGamma(g)
  const bLinear = invGamma(b)

  // Convert linear RGB to OKLab
  const l =
    0.4122214708 * rLinear + 0.5363325363 * gLinear + 0.0514459929 * bLinear
  const m =
    0.2119034982 * rLinear + 0.6806995451 * gLinear + 0.1073969566 * bLinear
  const s =
    0.0883024619 * rLinear + 0.2817188376 * gLinear + 0.6299787005 * bLinear

  // Apply non-linear transformation
  const lRoot = Math.cbrt(l)
  const mRoot = Math.cbrt(m)
  const sRoot = Math.cbrt(s)

  // Convert to OKLab
  const L = 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot
  const a = 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot
  const bLab = 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot

  // Convert OKLab to OKLCH
  const C = Math.sqrt(a * a + bLab * bLab)
  let H = (Math.atan2(bLab, a) * 180) / Math.PI
  if (H < 0) H += 360

  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(3)})`
}

/**
 * Darkens a color by a specified percentage
 * @param colorString - Color string in hex format (e.g., "#0d542b") or OKLCH format (e.g., "oklch(0.393 0.095 152.535)")
 * @param percentage - Percentage to darken (0-100)
 * @returns Darkened color in the same format as input
 */
export function darkenColor(colorString: string, percentage: number): string {
  if (percentage < 0 || percentage > 100) {
    throw new Error("Percentage must be between 0 and 100")
  }

  const isHex = colorString.startsWith("#")
  let oklchString: string

  // Convert to OKLCH if needed
  if (isHex) {
    oklchString = hexToOklch(colorString)
  } else {
    oklchString = colorString
  }

  // Parse OKLCH values
  const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/)
  if (!match) {
    throw new Error("Invalid color format")
  }

  let L = parseFloat(match[1]) // Lightness (0-1)
  const C = parseFloat(match[2]) // Chroma
  const H = parseFloat(match[3]) // Hue (degrees)

  // Darken by reducing lightness
  // Percentage represents how much to reduce (e.g., 20% means reduce L by 20% of its current value)
  const darkenFactor = percentage / 100
  L = L * (1 - darkenFactor)
  L = Math.max(0, Math.min(1, L)) // Clamp between 0 and 1

  const darkenedOklch = `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(3)})`

  // Convert back to original format
  if (isHex) {
    return oklchToHex(darkenedOklch)
  }

  return darkenedOklch
}

/**
 * Converts RGB values (0-1 range) to hexadecimal color string
 * @param rgb - Object with r, g, b values in range 0-1 (e.g., { r: 0.02, g: 0.04, b: 0.8 })
 * @returns Hexadecimal color string (e.g., "#0514cc")
 */
export function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  // Clamp values between 0 and 1
  const r = Math.max(0, Math.min(1, rgb.r))
  const g = Math.max(0, Math.min(1, rgb.g))
  const b = Math.max(0, Math.min(1, rgb.b))

  // Convert to 0-255 range and then to hex
  const toHex = (value: number) => {
    const hex = Math.round(value * 255).toString(16)
    return hex.length === 1 ? "0" + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Converts RGB values (0-255 range) to hexadecimal color string
 * @param rgb - Object with r, g, b values in range 0-255 (e.g. from getComputedStyle)
 * @returns Hexadecimal color string (e.g. "#0514cc")
 */
export function rgbToHex256(rgb: { r: number; g: number; b: number }): string {
  const r = Math.max(0, Math.min(255, Math.round(rgb.r)))
  const g = Math.max(0, Math.min(255, Math.round(rgb.g)))
  const b = Math.max(0, Math.min(255, Math.round(rgb.b)))
  const toHex = (value: number) => {
    const hex = value.toString(16)
    return hex.length === 1 ? "0" + hex : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
